// client/src/features/salesCoach/components/OrgAdminSalesCoachMain.jsx
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { salesCoachApi } from "../api/salesCoachApi";
import { Plus, Trash2, Edit, Brain, Users, PlayCircle } from "lucide-react";
import Loader from "@/shared/components/ui/Loaders/Loader";

// Views
import CreateEditTraining from "./CreateEditTraining";
import AITrainer from "./AITrainer";
// import MemoryRules from "./MemoryRules";
import TestCoach from "./TestCoach";
import PerformanceDashboard from "./PerformanceDashboard";

const OrgAdminSalesCoachMain = () => {
  const { tab, id } = useParams();

  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tab) {
      loadTrainings();
    }
  }, [tab]);

  const loadTrainings = async () => {
    setLoading(true);
    try {
      const data = await salesCoachApi.listTrainings();
      setTrainings(data);
    } catch (err) {
      console.error("Failed to load trainings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (trainingId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this training? This will delete all knowledge chunks, rules, and history.",
      )
    ) {
      try {
        await salesCoachApi.deleteTraining(trainingId);
        loadTrainings();
      } catch (err) {
        console.error("Failed to delete training:", err);
      }
    }
  };

  // Render sub-views depending on routing parameters
  if (tab === "create" || (tab === "edit" && id)) {
    return <CreateEditTraining tab={tab} id={id} />;
  }

  if (tab === "trainer" && id) {
    return <AITrainer id={id} />;
  }

  if (tab === "test" && id) {
    return <TestCoach id={id} />;
  }

  if (tab === "dashboard") {
    return <PerformanceDashboard />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-[calc(100vh-60px)] bg-gray-50/50">
      {/* Header section with sleek background */}
      <div className="flex flex-col md:flex-row md:items-center justify-between pb-6 border-b border-gray-200 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 bg-gradient-to-r from-secondary via-secondary-light to-[#058BB3] bg-clip-text text-transparent">
            AI Sales Coach
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Train, test, and manage organization-specific AI coaches for your
            sales team.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/oa/sales-coach/dashboard"
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none transition-all duration-200"
          >
            <Users className="w-4 h-4 mr-2 text-gray-500" />
            Learners Dashboard
          </Link>
          <Link
            to="/oa/sales-coach/create"
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-secondary border border-transparent rounded-lg shadow-sm hover:bg-secondary-light focus:outline-none transition-all duration-200 hover:shadow-[#207C9730] hover:shadow-lg"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Training
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-96">
          <Loader text="Loading trainings..." />
        </div>
      ) : (
        <div className="mt-8">
          {trainings.length === 0 ? (
            <div className="text-center py-16 px-4 bg-white border border-dashed border-gray-300 rounded-2xl shadow-sm">
              <Brain className="mx-auto h-12 w-12 text-secondary animate-pulse" />
              <h3 className="mt-4 text-lg font-bold text-gray-900">
                No trainings created yet
              </h3>
              <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
                Create a training module, add product specifications, and teach
                your AI Coach rules to help representatives learn.
              </p>
              <div className="mt-6">
                <Link
                  to="/oa/sales-coach/create"
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-secondary rounded-lg hover:bg-secondary-light"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Get Started
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {trainings.map((t) => (
                <div
                  key={t.id}
                  className="group bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col justify-between"
                >
                  <div className="p-6">
                    <div className="flex justify-between items-start gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-secondary-ultra-light text-secondary border border-[#207C9730]">
                        {t.category}
                      </span>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                          t.status === "published"
                            ? "bg-green-50 text-green-700 border-green-100"
                            : "bg-amber-50 text-amber-700 border-amber-100"
                        }`}
                      >
                        {t.status === "published" ? "Published" : "Draft"}
                      </span>
                    </div>

                    <h3 className="mt-4 text-xl font-bold text-gray-900 group-hover:text-secondary transition-colors duration-200">
                      {t.product_name}
                    </h3>
                    <p className="mt-2 text-sm text-gray-500 line-clamp-3">
                      {t.description || "No description provided."}
                    </p>

                    {/* Stats segment */}
                    <div className="mt-6 grid grid-cols-2 gap-4 py-3 px-4 bg-gray-50 rounded-xl border border-gray-100 text-xs">
                      <div>
                        <span className="block text-gray-400 font-medium">
                          Total Learners
                        </span>
                        <span className="text-base font-bold text-gray-800">
                          {t.total_learners || 0}
                        </span>
                      </div>
                      <div>
                        <span className="block text-gray-400 font-medium">
                          Avg Score
                        </span>
                        <span className="text-base font-bold text-gray-800">
                          {t.avg_assessment_score
                            ? `${t.avg_assessment_score}%`
                            : "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions segment */}
                  <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between gap-2">
                    <div className="flex gap-1.5">
                      <Link
                        to={`/oa/sales-coach/edit/${t.id}`}
                        title="Edit Content"
                        className="p-2 text-gray-500 hover:text-secondary hover:bg-secondary-ultra-light rounded-lg transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </Link>
                      <Link
                        to={`/oa/sales-coach/trainer/${t.id}`}
                        title="Train Coach"
                        className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      >
                        <Brain className="w-4 h-4" />
                      </Link>
                      <Link
                        to={`/oa/sales-coach/test/${t.id}`}
                        title="Test Coach"
                        className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                      >
                        <PlayCircle className="w-4 h-4" />
                      </Link>
                    </div>
                    <button
                      onClick={() => handleDelete(t.id)}
                      title="Delete Training"
                      className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrgAdminSalesCoachMain;
